import { LocationItem, LanguageItem } from '../services/api';

export interface VersionedCache<T> {
  version: string;
  timestamp: number;
  data: T;
}

export interface LocalStorageSchema {
  ClosedZeroMetricBannerOnClusters: string[];
  jwt_token: string;
  jwt_token_admin: string;
  ref_locations: VersionedCache<LocationItem[]>;
  ref_languages: VersionedCache<LanguageItem[]>;
}

export type LocalStorageKey = keyof LocalStorageSchema;

const fetchFunctions: {
  [K in keyof LocalStorageSchema]: () => Promise<LocalStorageSchema[K]>
} = {
  ClosedZeroMetricBannerOnClusters: () => Promise.resolve([] as LocalStorageSchema['ClosedZeroMetricBannerOnClusters']),
  jwt_token: () => Promise.resolve(''),
  jwt_token_admin: () => Promise.resolve(''),
  ref_locations: () => Promise.resolve({ version: '1', timestamp: Date.now(), data: [] }),
  ref_languages: () => Promise.resolve({ version: '1', timestamp: Date.now(), data: [] })
}

// Parse a stored value as JSON, but tolerate raw (non-JSON) strings.
function safeParse<T>(item: string): T {
  try {
    return JSON.parse(item) as T;
  } catch {
    // Only legacy raw-string values (e.g. an un-stringified jwt_token) land here. 
    // Returning the raw string is correct for string-typed keys; 
    // JSON-typed keys are always written via JSON.stringify and never reach this branch.
    return item as unknown as T;
  }
}

function createNamespace<T extends keyof LocalStorageSchema>(key: T) {
  return {
    get: (): LocalStorageSchema[T] | null => {
      if (typeof window === 'undefined') return null;

      const item = localStorage.getItem(key);
      return item ? safeParse<LocalStorageSchema[T]>(item) : null;
    },
    set: (value: LocalStorageSchema[T]): void => {
      if (typeof window === 'undefined') return;
      localStorage.setItem(key, JSON.stringify(value));
    },
    remove: (): void => {
      if (typeof window === 'undefined') return;
      localStorage.removeItem(key);
    },
    fetch: async (): Promise<LocalStorageSchema[T]> => {
      const item = localStorage.getItem(key)
      if (item) {
        return safeParse<LocalStorageSchema[T]>(item);
      }

      try {
        const data = await fetchFunctions[key]();
        if (typeof window !== 'undefined') {
          localStorage.setItem(key, JSON.stringify(data));
        }
        return data;
      } catch (error) {
        console.error(`Failed to fetch ${key}:`, error);
        throw error; // Re-throw to let caller handle
      }
    }
  }
}

export const MyLocalStorage = {
  ClosedZeroMetricBannerOnClusters: createNamespace("ClosedZeroMetricBannerOnClusters"),
  jwt_token: createNamespace("jwt_token"),
  jwt_token_admin: createNamespace("jwt_token_admin"),
  ref_locations: createNamespace("ref_locations"),
  ref_languages: createNamespace("ref_languages"),
}